import {
  collection,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { ServiceProvider, UserRole, UserStatus, type UserProfile } from '../types/firestore';
import { isOperationalServiceProvider } from '../utils/providerEligibility';
import { resolveServiceVerificationStatus } from '../utils/serviceVerification';
import { getServiceProviderProfilePath } from '../utils/serviceProviderProfilePath';

export const ADMIN_ACCOUNTS_PAGE_SIZE = 80;
export const ADMIN_ACCOUNTS_PROVIDER_SCAN_LIMIT = 120;

export type AdminAccountCursor = QueryDocumentSnapshot<DocumentData> | null;
export type AdminAccountRole = 'client' | 'serviceAdmin' | 'admin' | 'unknown';
export type AdminAccountStatus =
  | 'active'
  | 'pending'
  | 'pending_verification'
  | 'suspended'
  | 'unknown';
export type AdminCapabilityState =
  | 'admin_authz_record'
  | 'service_admin_authz_record'
  | 'no_authz_record'
  | 'unavailable';
export type AdminAccountRiskFlag =
  | 'suspended'
  | 'pending'
  | 'incompleteProfile'
  | 'serviceAdminWithoutProvider'
  | 'providerWithoutUser'
  | 'adminIdentityWithoutAuthzVisibility'
  | 'unknownRole'
  | 'unknownStatus';

export interface AdminAccountsMetric {
  value: number | null;
  label: string;
  description: string;
  partial?: boolean;
}

export interface AdminAccountsMetrics {
  total: AdminAccountsMetric;
  clients: AdminAccountsMetric;
  serviceAdmins: AdminAccountsMetric;
  adminIdentities: AdminAccountsMetric;
  active: AdminAccountsMetric;
  pending: AdminAccountsMetric;
  suspended: AdminAccountsMetric;
  withRisks: AdminAccountsMetric;
}

export interface AdminAccountProviderSummary {
  uid: string;
  serviceName: string;
  verificationStatus: string;
  verificationStatusLabel: string;
  isVerified: boolean;
  isOperational: boolean;
  publicProfilePath: string | null;
}

export interface AdminOrphanProviderPreview {
  uid: string;
  serviceName: string;
  ownerEmail: string | null;
  verificationStatus: string;
  isVerified: boolean;
}

export interface AdminAccountDirectoryItem {
  uid: string;
  email: string | null;
  displayName: string | null;
  displayNameResolved: string;
  role: AdminAccountRole;
  roleRaw: string | null;
  roleLabel: string;
  status: AdminAccountStatus;
  statusRaw: string | null;
  statusLabel: string;
  capabilityState: AdminCapabilityState;
  capabilityLabel: string;
  capabilityDescription: string;
  provider: AdminAccountProviderSummary | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  createdAtLabel: string;
  updatedAtLabel: string;
  profileCompletenessLabel: string;
  riskFlags: AdminAccountRiskFlag[];
  riskLabels: string[];
  searchText: string;
  rawUser: Partial<UserProfile> & Record<string, unknown>;
}

export interface AdminAccountsReadModel {
  metrics: AdminAccountsMetrics;
  accounts: AdminAccountDirectoryItem[];
  orphanProviderPreviews: AdminOrphanProviderPreview[];
  cursor: AdminAccountCursor;
  hasMore: boolean;
  isPartial: boolean;
  partialError: string | null;
  authzReadState: {
    adminAuthorizations: 'unavailable';
    serviceAdminAuthorizations: 'unavailable';
    reason: string;
  };
  readAt: Date;
  readLimits: {
    pageSize: number;
    providerScanLimit: number;
  };
}

const metric = (
  value: number | null,
  label: string,
  description: string,
  partial = false
): AdminAccountsMetric => ({
  value,
  label,
  description,
  partial,
});

const unavailableAuthzReason =
  'Firestore rules oznaczają adminAuthorizations i serviceAdminAuthorizations jako backend-only; frontend pokazuje rekordy capability jako niedostępne.';

const emptyMetrics = (): AdminAccountsMetrics => ({
  total: metric(null, 'Wszystkie konta', 'Liczba dokumentów users', true),
  clients: metric(null, 'Klienci', "users.role == 'client'", true),
  serviceAdmins: metric(null, 'ServiceAdmini', "users.role == 'serviceAdmin'", true),
  adminIdentities: metric(null, 'Admin identity', "users.role == 'admin'", true),
  active: metric(null, 'Aktywne', "users.status == 'active'", true),
  pending: metric(null, 'Pending', "users.status == 'pending' lub 'pending_verification'", true),
  suspended: metric(null, 'Suspended', "users.status == 'suspended'", true),
  withRisks: metric(null, 'Konta z ryzykami', 'Ryzyka w ograniczonym odczycie kont', true),
});

const countCollection = async (
  collectionName: string,
  ...constraints: QueryConstraint[]
): Promise<number> => {
  const snapshot = await getCountFromServer(
    query(collection(firestore, collectionName), ...constraints)
  );
  return snapshot.data().count;
};

const trimString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const normalizeTimestamp = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof (value as { toDate?: unknown }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (
    typeof (value as { seconds?: unknown }).seconds === 'number' &&
    Number.isFinite((value as { seconds: number }).seconds)
  ) {
    return new Date((value as { seconds: number }).seconds * 1000);
  }
  return null;
};

const formatDateTime = (date: Date | null): string =>
  date
    ? new Intl.DateTimeFormat('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    : 'Brak daty';

export const resolveAccountDisplayName = (
  user: Partial<UserProfile> & Record<string, unknown>,
  uid: string
): string => trimString(user.displayName) || trimString(user.email) || uid;

export const classifyAdminAccountRuntime = (
  user: Partial<UserProfile> & Record<string, unknown>
): {
  role: AdminAccountRole;
  roleRaw: string | null;
  status: AdminAccountStatus;
  statusRaw: string | null;
} => {
  const roleRaw = trimString(user.role);
  const statusRaw = trimString(user.status);
  const role: AdminAccountRole =
    roleRaw === UserRole.CLIENT
      ? 'client'
      : roleRaw === UserRole.SERVICE_ADMIN
        ? 'serviceAdmin'
        : roleRaw === UserRole.ADMIN
          ? 'admin'
          : 'unknown';
  const status: AdminAccountStatus =
    statusRaw === UserStatus.ACTIVE
      ? 'active'
      : statusRaw === UserStatus.PENDING
        ? 'pending'
        : statusRaw === UserStatus.PENDING_VERIFICATION
          ? 'pending_verification'
          : statusRaw === UserStatus.SUSPENDED
            ? 'suspended'
            : 'unknown';

  return { role, roleRaw, status, statusRaw };
};

const getRoleLabel = (role: AdminAccountRole, roleRaw: string | null): string => {
  if (role === 'client') return 'Client';
  if (role === 'serviceAdmin') return 'ServiceAdmin';
  if (role === 'admin') return 'Admin identity';
  return roleRaw ? `Unknown: ${roleRaw}` : 'Unknown role';
};

const getStatusLabel = (status: AdminAccountStatus, statusRaw: string | null): string => {
  if (status === 'active') return 'Active';
  if (status === 'pending') return 'Pending';
  if (status === 'pending_verification') return 'Pending verification';
  if (status === 'suspended') return 'Suspended';
  return statusRaw ? `Unknown: ${statusRaw}` : 'Unknown status';
};

export const resolveAccountCapabilitySummary = (
  role: AdminAccountRole
): Pick<
  AdminAccountDirectoryItem,
  'capabilityState' | 'capabilityLabel' | 'capabilityDescription'
> => {
  if (role === 'admin') {
    return {
      capabilityState: 'unavailable',
      capabilityLabel: 'Admin authz unavailable',
      capabilityDescription:
        'Actual admin claim innych użytkowników i adminAuthorizations nie są czytelne z frontendu.',
    };
  }
  if (role === 'serviceAdmin') {
    return {
      capabilityState: 'unavailable',
      capabilityLabel: 'Service authz unavailable',
      capabilityDescription:
        'Actual serviceAdmin claim i serviceAdminAuthorizations nie są czytelne z frontendu.',
    };
  }
  return {
    capabilityState: 'unavailable',
    capabilityLabel: 'Capability unavailable',
    capabilityDescription:
      'Widok nie ma backendowego read-modelu custom claims innych użytkowników.',
  };
};

const hasCompleteUserProfile = (user: Partial<UserProfile> & Record<string, unknown>): boolean =>
  Boolean(trimString(user.email) || trimString(user.displayName));

export const getAccountRiskFlags = (account: {
  role: AdminAccountRole;
  status: AdminAccountStatus;
  provider: AdminAccountProviderSummary | null;
  hasCompleteProfile: boolean;
}): AdminAccountRiskFlag[] => {
  const flags: Array<AdminAccountRiskFlag | null> = [
    account.status === 'suspended' ? 'suspended' : null,
    account.status === 'pending' || account.status === 'pending_verification' ? 'pending' : null,
    !account.hasCompleteProfile ? 'incompleteProfile' : null,
    account.role === 'serviceAdmin' && !account.provider ? 'serviceAdminWithoutProvider' : null,
    account.role === 'admin' ? 'adminIdentityWithoutAuthzVisibility' : null,
    account.role === 'unknown' ? 'unknownRole' : null,
    account.status === 'unknown' ? 'unknownStatus' : null,
  ];
  return flags.filter((flag): flag is AdminAccountRiskFlag => Boolean(flag));
};

export const getAccountRiskLabel = (flag: AdminAccountRiskFlag): string => {
  const labels: Record<AdminAccountRiskFlag, string> = {
    suspended: 'Konto suspended',
    pending: 'Konto pending',
    incompleteProfile: 'Niekompletny profil konta',
    serviceAdminWithoutProvider: 'ServiceAdmin bez provider profile',
    providerWithoutUser: 'Provider bez user context',
    adminIdentityWithoutAuthzVisibility: 'Admin identity bez widocznego authz',
    unknownRole: 'Nieznana rola',
    unknownStatus: 'Nieznany status',
  };
  return labels[flag];
};

const providerDocToSummary = (
  uid: string,
  data: Record<string, unknown>
): AdminAccountProviderSummary => {
  const provider = { uid, ...data } as Partial<ServiceProvider> & Record<string, unknown>;
  const verificationStatus = resolveServiceVerificationStatus(provider) || 'unknown';
  const isVerified = provider.isVerified === true;
  const isOperational = isOperationalServiceProvider(provider);
  return {
    uid,
    serviceName: trimString(provider.serviceName) || 'Brak nazwy serwisu',
    verificationStatus,
    verificationStatusLabel:
      verificationStatus === 'approved'
        ? 'Approved'
        : verificationStatus === 'pending_approval'
          ? 'Pending'
          : verificationStatus === 'changes_requested'
            ? 'Do poprawek'
            : verificationStatus === 'profile_incomplete'
              ? 'Niekompletny'
              : 'Unknown',
    isVerified,
    isOperational,
    publicProfilePath:
      verificationStatus === 'approved' && isVerified ? getServiceProviderProfilePath(uid) : null,
  };
};

export const userDocToAccountItem = (
  uid: string,
  data: Record<string, unknown>,
  provider: AdminAccountProviderSummary | null = null
): AdminAccountDirectoryItem => {
  const user = { uid, ...data } as Partial<UserProfile> & Record<string, unknown>;
  const runtime = classifyAdminAccountRuntime(user);
  const createdAt = normalizeTimestamp(user.createdAt);
  const updatedAt = normalizeTimestamp(user.updatedAt);
  const hasCompleteProfile = hasCompleteUserProfile(user);
  const capability = resolveAccountCapabilitySummary(runtime.role);
  const riskFlags = getAccountRiskFlags({
    role: runtime.role,
    status: runtime.status,
    provider,
    hasCompleteProfile,
  });

  const email = trimString(user.email);
  const displayName = trimString(user.displayName);
  const displayNameResolved = resolveAccountDisplayName(user, uid);

  return {
    uid,
    email,
    displayName,
    displayNameResolved,
    role: runtime.role,
    roleRaw: runtime.roleRaw,
    roleLabel: getRoleLabel(runtime.role, runtime.roleRaw),
    status: runtime.status,
    statusRaw: runtime.statusRaw,
    statusLabel: getStatusLabel(runtime.status, runtime.statusRaw),
    ...capability,
    provider,
    createdAt,
    updatedAt,
    createdAtLabel: formatDateTime(createdAt),
    updatedAtLabel: formatDateTime(updatedAt),
    profileCompletenessLabel: hasCompleteProfile
      ? 'Podstawowy profil obecny'
      : 'Brak email/displayName',
    riskFlags,
    riskLabels: riskFlags.map(getAccountRiskLabel),
    searchText: [uid, email, displayName].filter(Boolean).join(' ').toLowerCase(),
    rawUser: user,
  };
};

const loadProviderSummariesForUsers = async (
  users: Array<{ uid: string; role: AdminAccountRole }>
): Promise<Map<string, AdminAccountProviderSummary>> => {
  const serviceAdminUids = users
    .filter((user) => user.role === 'serviceAdmin')
    .map((user) => user.uid);
  const entries = await Promise.all(
    serviceAdminUids.map(async (uid) => {
      const snapshot = await getDoc(doc(firestore, 'serviceProviders', uid));
      return snapshot.exists()
        ? ([uid, providerDocToSummary(uid, snapshot.data())] as const)
        : null;
    })
  );
  return new Map(
    entries.filter((entry): entry is readonly [string, AdminAccountProviderSummary] =>
      Boolean(entry)
    )
  );
};

const loadOrphanProviderPreviews = async (): Promise<AdminOrphanProviderPreview[]> => {
  const providersSnapshot = await getDocs(
    query(
      collection(firestore, 'serviceProviders'),
      orderBy('updatedAt', 'desc'),
      limit(ADMIN_ACCOUNTS_PROVIDER_SCAN_LIMIT)
    )
  );
  const candidates = providersSnapshot.docs.slice(0, 24);
  const previews: Array<AdminOrphanProviderPreview | null> = await Promise.all(
    candidates.map(async (providerSnap) => {
      const userSnap = await getDoc(doc(firestore, 'users', providerSnap.id));
      if (userSnap.exists()) return null;
      const provider = providerSnap.data() as Partial<ServiceProvider> & Record<string, unknown>;
      return {
        uid: providerSnap.id,
        serviceName: trimString(provider.serviceName) || 'Brak nazwy serwisu',
        ownerEmail: trimString(provider.email),
        verificationStatus: String(resolveServiceVerificationStatus(provider) || 'unknown'),
        isVerified: provider.isVerified === true,
      };
    })
  );
  return previews.filter((preview): preview is AdminOrphanProviderPreview => Boolean(preview));
};

const loadAccountMetrics = async (): Promise<AdminAccountsMetrics> => {
  const [
    total,
    clients,
    serviceAdmins,
    adminIdentities,
    active,
    pending,
    pendingVerification,
    suspended,
  ] = await Promise.all([
    countCollection('users'),
    countCollection('users', where('role', '==', UserRole.CLIENT)),
    countCollection('users', where('role', '==', UserRole.SERVICE_ADMIN)),
    countCollection('users', where('role', '==', UserRole.ADMIN)),
    countCollection('users', where('status', '==', UserStatus.ACTIVE)),
    countCollection('users', where('status', '==', UserStatus.PENDING)),
    countCollection('users', where('status', '==', UserStatus.PENDING_VERIFICATION)),
    countCollection('users', where('status', '==', UserStatus.SUSPENDED)),
  ]);

  return {
    total: metric(total, 'Wszystkie konta', 'Liczba dokumentów users'),
    clients: metric(clients, 'Klienci', "users.role == 'client'"),
    serviceAdmins: metric(serviceAdmins, 'ServiceAdmini', "users.role == 'serviceAdmin'"),
    adminIdentities: metric(adminIdentities, 'Admin identity', "users.role == 'admin'"),
    active: metric(active, 'Aktywne', "users.status == 'active'"),
    pending: metric(
      pending + pendingVerification,
      'Pending',
      "users.status == 'pending' lub 'pending_verification'"
    ),
    suspended: metric(suspended, 'Suspended', "users.status == 'suspended'"),
    withRisks: metric(null, 'Konta z ryzykami', 'Liczone na aktualnie wczytanej stronie', true),
  };
};

const loadAccountPage = async (
  pageSize: number,
  cursor: AdminAccountCursor
): Promise<{
  accounts: AdminAccountDirectoryItem[];
  cursor: AdminAccountCursor;
  hasMore: boolean;
}> => {
  const constraints: QueryConstraint[] = [orderBy(documentId()), limit(pageSize)];
  if (cursor) {
    constraints.splice(1, 0, startAfter(cursor));
  }
  const snapshot = await getDocs(query(collection(firestore, 'users'), ...constraints));
  const userRuntime = snapshot.docs.map((docSnap) => ({
    uid: docSnap.id,
    role: classifyAdminAccountRuntime(docSnap.data()).role,
  }));
  const providerSummaries = await loadProviderSummariesForUsers(userRuntime);
  const accounts = snapshot.docs.map((docSnap) =>
    userDocToAccountItem(docSnap.id, docSnap.data(), providerSummaries.get(docSnap.id) || null)
  );

  return {
    accounts,
    cursor: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.size >= pageSize,
  };
};

export const getAdminAccountsReadModel = async (
  pageSize = ADMIN_ACCOUNTS_PAGE_SIZE,
  cursor: AdminAccountCursor = null
): Promise<AdminAccountsReadModel> => {
  const [metricsResult, pageResult, orphanProvidersResult] = await Promise.allSettled([
    loadAccountMetrics(),
    loadAccountPage(pageSize, cursor),
    loadOrphanProviderPreviews(),
  ]);

  if (pageResult.status === 'rejected') {
    throw pageResult.reason;
  }

  const page = pageResult.value;
  const metrics = metricsResult.status === 'fulfilled' ? metricsResult.value : emptyMetrics();
  metrics.withRisks = metric(
    page.accounts.filter((account) => account.riskFlags.length > 0).length,
    'Konta z ryzykami',
    'Ryzyka na aktualnie wczytanej stronie',
    page.hasMore
  );

  const partialMessages = [
    metricsResult.status === 'rejected' ? 'Nie udało się odczytać części metryk kont.' : null,
    orphanProvidersResult.status === 'rejected'
      ? 'Nie udało się odczytać podglądu providerów bez user context.'
      : null,
  ].filter(Boolean);

  return {
    metrics,
    accounts: page.accounts,
    orphanProviderPreviews:
      orphanProvidersResult.status === 'fulfilled' ? orphanProvidersResult.value : [],
    cursor: page.cursor,
    hasMore: page.hasMore,
    isPartial:
      partialMessages.length > 0 ||
      metrics.withRisks.partial === true ||
      Object.values(metrics).some((entry) => entry.partial),
    partialError: partialMessages.join(' ') || null,
    authzReadState: {
      adminAuthorizations: 'unavailable',
      serviceAdminAuthorizations: 'unavailable',
      reason: unavailableAuthzReason,
    },
    readAt: new Date(),
    readLimits: {
      pageSize,
      providerScanLimit: ADMIN_ACCOUNTS_PROVIDER_SCAN_LIMIT,
    },
  };
};
